import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Archive, ArrowLeft, ChevronRight, ClipboardPlus, History, Pencil } from 'lucide-react';
import {
  fetchLineaFicha, fetchMencionesLinea, actualizarLinea, darDeBajaLinea, subirFotoLinea, crearEvento, anularEvento,
} from 'services/cablesService';
import { plazo, diaMes } from 'lib/arboles';
import {
  estadoDesdeEventos, eventosOrdenados, mencionesDeLinea, historialLinea, cambiosLinea, idLineaValido, valorCampo,
  puedeEditarCables, tramo, tipoEventoLabel, textoAnios,
} from 'lib/cables';
import { fechaCorta, fechaHoraCorta, nombreUsuario, plural } from 'lib/auditoria';
import { etiquetaInstalacion, etiquetasDe } from 'lib/auditoriaInstalaciones';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import { ESTADO_ICONO, ESTADO_LABEL } from 'lib/estadoUi';
import { hoyCR } from 'lib/fechaCR';
import { useAuth } from 'contexts/AuthContext';
import AppShell from 'components/AppShell';
import Cargando from 'components/Cargando';
import useNombres from 'components/useNombres';
import { ResultadoDiscreto, MarcaRevision } from 'components/ResultadoCable';
import MotivoDialog from 'components/MotivoDialog';
import Button from 'components/ui/Button';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import { BotonFoto, MenuMas, Seccion } from 'pages/arboles/FichaPartes';
import FormCable from './FormCable';
import FormEvento from './FormEvento';
import DatosCable from './DatosCable';
import LineaEventos from './LineaEventos';
import { mensajeError } from 'lib/errores';

// Celda de la franja de resumen: etiqueta chica, valor destacado y una línea
// de apoyo atenuada.
function Celda({ label, children, apoyo, className = '' }) {
  return (
    <div className={`min-w-0 bg-card px-4 py-3 sm:px-5 ${className}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold leading-tight text-foreground">{children}</div>
      {apoyo && <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{apoyo}</div>}
    </div>
  );
}

// Vida útil: barra fina y discreta con lo consumido; pasada, en error.
function BarraVida({ usado, vencido }) {
  if (usado === null || usado === undefined) return null;
  const pct = Math.round(usado * 100);
  return (
    <div className="mt-1.5 h-1 w-full max-w-[9rem] overflow-hidden rounded-full bg-muted" role="img"
      aria-label={`Vida útil consumida: ${pct} %`}>
      <div className={`h-full rounded-full ${vencido ? 'bg-error/50' : usado > 0.8 ? 'bg-warning/70' : 'bg-primary/50'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function EstadoMencion({ estado }) {
  const Icono = ESTADO_ICONO[estado];
  const color = estado === 'falla' ? 'text-error' : estado === 'alerta' ? 'text-warning' : 'text-success';
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
      {Icono && <Icono size={13} strokeWidth={2.5} className={color} aria-hidden="true" />}
      {ESTADO_LABEL[estado] || 'Sin dato'}
    </span>
  );
}

export default function CableFicha() {
  const { id } = useParams();
  const { role } = useAuth();
  const puedeEditar = puedeEditarCables(role);
  const esAdmin = role === 'admin';
  const hoy = hoyCR();
  const [datos, setDatos] = useState({ cargando: true, error: null, linea: null, eventos: [], historial: [], errorHistorial: null });
  const [menciones, setMenciones] = useState({ cargando: true, error: null, filas: [] });
  const [editar, setEditar] = useState(false);
  const [registrar, setRegistrar] = useState(false);
  const [dialogo, setDialogo] = useState(null); // { tipo: 'baja' } | { tipo: 'anular', evento }
  const [foto, setFoto] = useState({ subiendo: false, error: '' });

  const cargar = useCallback(async () => {
    if (!idLineaValido(id)) {
      setDatos({ cargando: false, error: null, linea: null, eventos: [], historial: [], errorHistorial: null });
      return;
    }
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      const r = await fetchLineaFicha(id);
      setDatos({ cargando: false, error: null, ...r });
    } catch (e) {
      setDatos({ cargando: false, error: e, linea: null, eventos: [], historial: [], errorHistorial: null });
    }
  }, [id]);

  const cargarMenciones = useCallback(async () => {
    if (!idLineaValido(id)) return;
    setMenciones((m) => ({ ...m, cargando: true, error: null }));
    try {
      setMenciones({ cargando: false, error: null, filas: await fetchMencionesLinea(id) });
    } catch (e) {
      setMenciones({ cargando: false, error: e, filas: [] });
    }
  }, [id]);

  useEffect(() => { cargar(); cargarMenciones(); }, [cargar, cargarMenciones]);

  const { linea, eventos, historial } = datos;
  const estado = useMemo(() => estadoDesdeEventos(eventos, linea, hoy), [eventos, linea, hoy]);
  const evs = useMemo(() => eventosOrdenados(eventos), [eventos]);
  const cambios = useMemo(() => historialLinea(historial, linea), [historial, linea]);
  const lista = useMemo(() => mencionesDeLinea(menciones.filas, id), [menciones.filas, id]);
  const etiquetas = useMemo(() => etiquetasDe(linea?.instalacion), [linea]);
  const nombres = useNombres(useMemo(() => [
    linea?.created_by, linea?.updated_by, ...eventos.flatMap((e) => [e.created_by, e.anulada_por]),
    ...cambios.map((c) => c.por),
  ], [linea, eventos, cambios]));
  // Códigos de los árboles para el historial (los actuales de la ficha).
  const codigosArbol = useMemo(() => ({
    ...(linea?.arbol_desde_id ? { [linea.arbol_desde_id]: linea.arbol_desde_codigo } : {}),
    ...(linea?.arbol_hasta_id ? { [linea.arbol_hasta_id]: linea.arbol_hasta_codigo } : {}),
  }), [linea]);

  const activo = linea?.activo !== false;

  const guardarFicha = async (d) => {
    const c = cambiosLinea(linea, d);
    if (!Object.keys(c).length) return;
    await actualizarLinea(linea.id, c);
    await cargar();
  };

  const guardarEvento = async (d, informe) => {
    const nuevo = await crearEvento(linea.id, d, informe);
    setDatos((x) => ({ ...x, eventos: [nuevo, ...x.eventos] }));
    return nuevo;
  };

  const actualizarFechaInstalacion = async (fecha) => {
    await actualizarLinea(linea.id, { fecha_instalacion: fecha }, 'No se pudo actualizar la fecha de instalación');
    await cargar();
  };

  const cambiarFoto = async (archivo) => {
    setFoto({ subiendo: true, error: '' });
    try {
      await subirFotoLinea(linea.id, archivo);
      setFoto({ subiendo: false, error: '' });
      await cargar();
    } catch (e) {
      setFoto({ subiendo: false, error: mensajeError(e, 'No se pudo subir la foto.') });
    }
  };

  const confirmarDialogo = async (motivo) => {
    if (dialogo.tipo === 'baja') {
      await darDeBajaLinea(linea.id, motivo);
      await cargar();
    } else {
      const fila = await anularEvento(dialogo.evento.id, motivo);
      setDatos((x) => ({ ...x, eventos: x.eventos.map((e) => (e.id === fila.id ? { ...e, ...fila } : e)) }));
    }
  };

  const volver = (
    <Link to={linea ? `/cables${linea.instalacion && linea.instalacion !== 'canopy' ? `?instalacion=${linea.instalacion}` : ''}` : '/cables'}
      className="-mt-2 mb-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
      <ArrowLeft size={14} aria-hidden="true" /> Cables
    </Link>
  );

  if (datos.cargando) return <AppShell titulo="Cable">{volver}<Cargando texto="Cargando la ficha…" /></AppShell>;
  if (datos.error) {
    return (
      <AppShell titulo="Cable">
        {volver}
        <ErrorState title="No se pudo cargar el cable" message={mensajeError(datos.error)} onRetry={cargar} />
      </AppShell>
    );
  }
  if (!linea) {
    return (
      <AppShell titulo="Cable">
        {volver}
        <EmptyState framed title="Cable no encontrado" description="El enlace no corresponde a ningún cable registrado o su usuario no tiene acceso." />
      </AppShell>
    );
  }

  const t = tramo(linea);
  const opcionesMas = [
    ...(puedeEditar && activo ? [{ id: 'editar', label: 'Editar ficha', icono: Pencil, onClick: () => setEditar(true) }] : []),
    ...(esAdmin && activo ? [{ id: 'baja', label: 'Dar de baja', icono: Archive, peligro: true, onClick: () => setDialogo({ tipo: 'baja' }) }] : []),
  ];
  const ultimaMencion = lista[0];
  const vidaTexto = estado.restante === null ? null
    : estado.restante < 0 ? `Vencida hace ${textoAnios(estado.restante)}` : textoAnios(estado.restante);

  return (
    <AppShell>
      {volver}

      {/* Encabezado: código grande, tramo y estado; acciones compactas. */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <h1 className="font-heading text-5xl font-bold leading-none tracking-tight text-foreground tabular-nums sm:text-6xl">
            <span className="sr-only">Cable </span>{linea.codigo}
          </h1>
          <div className="min-w-0 pt-0.5">
            <p className={`text-xl font-semibold leading-tight ${t ? 'text-foreground' : 'text-muted-foreground'}`}>{t || 'Tramo sin registrar'}</p>
            {linea.nombre && <p className="mt-0.5 text-sm text-muted-foreground">{linea.nombre}</p>}
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>{etiquetaInstalacion(linea.instalacion)}</span>
              {activo && <span className="border-l border-border pl-3"><ResultadoDiscreto resultado={estado.resultado} /></span>}
            </p>
          </div>
        </div>
        {activo && (puedeEditar || opcionesMas.length > 0) && (
          <div className="flex shrink-0 items-center gap-2">
            {puedeEditar && (
              <Button size="sm" className="flex-1 sm:flex-none" onClick={() => setRegistrar(true)}>
                <ClipboardPlus size={15} className="mr-1.5" aria-hidden="true" />Nuevo evento
              </Button>
            )}
            <MenuMas opciones={opcionesMas} />
          </div>
        )}
      </header>

      <div className="mt-6 space-y-5">
        {!activo && (
          <p className="rounded-xl border border-border bg-muted px-4 py-3 text-sm text-foreground" data-testid="cable-baja">
            Dado de baja{linea.baja_at ? ` el ${fechaHoraCorta(linea.baja_at)}` : ''}: {linea.baja_motivo || 'sin motivo'}. La ficha y sus eventos se conservan.
          </p>
        )}

        {/* Resumen en una franja: resultado, último evento, próxima, vida útil e inspección diaria. */}
        <section aria-label="Resumen del cable" data-testid="estado-cable"
          className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border lg:grid-cols-5">
          <Celda label="Resultado vigente"
            apoyo={estado.resultado_fecha ? `${tipoEventoLabel(estado.resultado_tipo)} del ${diaMes(estado.resultado_fecha)} ${String(estado.resultado_fecha).slice(0, 4)}` : (estado.ultimo ? 'Ningún evento lo clasificó' : 'Sin eventos')}>
            <ResultadoDiscreto resultado={estado.resultado} grande textoVacio="Sin resultado" />
          </Celda>
          <Celda label="Último evento" apoyo={estado.ultimo ? `${tipoEventoLabel(estado.ultimo.tipo)} · ${estado.ultimo.responsable_nombre || 'Sin nombre'}` : null}>
            {estado.ultimo ? fechaCorta(estado.ultimo.fecha) : <span className="font-normal text-muted-foreground">Ninguno</span>}
          </Celda>
          <Celda label="Próxima revisión"
            apoyo={estado.vencida ? <MarcaRevision /> : estado.proxima_fecha ? plazo(hoy, estado.proxima_fecha) : null}>
            {estado.proxima_fecha ? fechaCorta(estado.proxima_fecha) : <span className="font-normal text-muted-foreground">Sin definir</span>}
          </Celda>
          <Celda label="Vida útil restante"
            apoyo={estado.vida !== null && estado.edad !== null ? `${textoAnios(estado.edad)} de ${textoAnios(estado.vida)}`
              : !linea.fecha_instalacion ? 'Falta la fecha de instalación' : 'Falta la vida útil'}>
            {vidaTexto ? <span className={estado.cambio_vencido ? 'text-error' : ''}>{vidaTexto}</span>
              : <span className="font-normal text-muted-foreground">Sin dato</span>}
            <BarraVida usado={estado.usado} vencido={estado.cambio_vencido} />
          </Celda>
          <Celda label="Inspección diaria" className="col-span-2 lg:col-span-1"
            apoyo={ultimaMencion ? `La última el ${diaMes(ultimaMencion.fecha)} ${String(ultimaMencion.fecha).slice(0, 4)}` : menciones.cargando || menciones.error ? null : 'Sin alertas ni fallas'}>
            {menciones.cargando ? <span className="font-normal text-muted-foreground">Buscando…</span>
              : menciones.error ? <span className="font-normal text-muted-foreground">Sin datos</span>
                : lista.length ? <a href="#t-menciones" className="hover:underline">{plural(new Set(lista.map((m) => m.inspeccion_id)).size, 'mención', 'menciones')}</a>
                  : <span className="font-normal text-muted-foreground">Ninguna</span>}
          </Celda>
        </section>

        <Seccion id="t-datos" titulo="Datos del cable"
          acciones={puedeEditar && activo && <BotonFoto tieneFoto={!!linea.foto_path} onElegir={cambiarFoto} subiendo={foto.subiendo} etiqueta="Foto del cable" />}>
          <DatosCable linea={linea} hoy={hoy} nombres={nombres} errorFoto={foto.error} />
        </Seccion>

        <Seccion id="t-eventos" titulo="Inspecciones y mantenimiento" detalle={evs.length || null}>
          {evs.length === 0 ? (
            <EmptyState size="sm" title="Sin eventos registrados."
              description={puedeEditar && activo ? 'Use "Nuevo evento" para registrar la inspección anual, una revisión o un mantenimiento.' : undefined} />
          ) : (
            <LineaEventos eventos={evs} hoy={hoy} esAdmin={esAdmin} nombres={nombres}
              onAnular={(x) => setDialogo({ tipo: 'anular', evento: x })} />
          )}
        </Seccion>

        <Seccion id="t-menciones" titulo="En la inspección diaria" detalle={lista.length || null}>
          {menciones.error ? (
            <ErrorState size="sm" title="No se pudieron cargar las menciones" message={mensajeError(menciones.error)} onRetry={cargarMenciones} />
          ) : menciones.cargando ? (
            <Cargando className="py-6" texto="Buscando menciones…" />
          ) : lista.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ninguna inspección diaria ha marcado este cable en alerta o falla.</p>
          ) : (
            <>
              <ul className="-my-2 divide-y divide-border/70">
                {lista.map((m) => (
                  <li key={`${m.inspeccion_id}:${m.itemKey}`} data-testid="mencion"
                    className="grid gap-x-4 gap-y-1 py-3 sm:grid-cols-[8.5rem_5.5rem_minmax(0,1fr)_auto] sm:items-baseline">
                    <span className="text-sm font-semibold tabular-nums text-foreground">{fechaCorta(m.fecha)}</span>
                    <span>{m.estado && <EstadoMencion estado={m.estado} />}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{etiquetas[m.itemKey] || m.itemKey}</p>
                      {m.nota && <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{m.nota}</p>}
                      <p className="mt-0.5 text-xs text-muted-foreground">{m.guia || 'Sin nombre'}</p>
                    </div>
                    <Link to={rutaInspeccion(m.inspeccion_id)} className="inline-flex items-center gap-0.5 text-sm font-medium text-primary underline-offset-2 hover:underline">
                      Ver inspección <ChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">{plural(lista.length, 'mención', 'menciones')} en inspecciones vigentes.</p>
            </>
          )}
        </Seccion>

        <Seccion id="t-historial" titulo="Historial de cambios" detalle={cambios.length || null}>
          {datos.errorHistorial ? (
            <p className="text-sm text-muted-foreground">{mensajeError(datos.errorHistorial)}</p>
          ) : cambios.length === 0 ? (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground"><History size={14} aria-hidden="true" />La ficha no se ha modificado desde que se registró.</p>
          ) : (
            <ol className="-my-2 divide-y divide-border/70">
              {cambios.map((c) => (
                <li key={c.id} data-testid="cambio" className="grid gap-x-4 gap-y-1.5 py-3 sm:grid-cols-[11rem_minmax(0,1fr)]">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{c.at ? fechaHoraCorta(c.at) : 'Sin fecha'}</p>
                    <p className="text-xs text-muted-foreground">{c.por ? nombreUsuario(c.por, nombres) : 'Sin dato'}</p>
                  </div>
                  {c.cambios.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin cambios visibles en la ficha.</p>
                  ) : (
                    <dl className="space-y-1.5">
                      {c.cambios.map((x) => (
                        <div key={x.campo} className="grid gap-x-3 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
                          <dt className="text-xs text-muted-foreground sm:pt-0.5">{x.label}</dt>
                          <dd className="min-w-0 break-words text-foreground">
                            <span className="text-muted-foreground line-through">{valorCampo(x.campo, x.antes, codigosArbol)}</span>
                            <span className="mx-1.5 text-muted-foreground" aria-hidden="true">→</span>
                            <span className="sr-only"> cambió a </span>
                            {valorCampo(x.campo, x.despues, codigosArbol)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ol>
          )}
        </Seccion>
      </div>

      <FormCable open={editar} onClose={() => setEditar(false)} linea={linea} onGuardar={guardarFicha} />
      <FormEvento open={registrar} onClose={() => setRegistrar(false)} linea={linea} onGuardar={guardarEvento}
        onFechaInstalacion={puedeEditar ? actualizarFechaInstalacion : null} />
      <MotivoDialog open={!!dialogo} onClose={() => setDialogo(null)} peligro
        titulo={dialogo?.tipo === 'baja' ? `Dar de baja el cable ${linea.codigo}` : 'Anular evento'}
        confirmar={dialogo?.tipo === 'baja' ? 'Dar de baja' : 'Anular evento'}
        etiqueta={dialogo?.tipo === 'baja' ? 'Motivo de la baja' : 'Motivo de la anulación'}
        descripcion={dialogo?.tipo === 'baja'
          ? 'El cable deja de aparecer en la lista y en la inspección diaria. La ficha, sus eventos y su historial se conservan.'
          : dialogo && <>El evento del {fechaCorta(dialogo.evento.fecha)} deja de contar para el estado del cable. Queda guardado con su motivo.</>}
        onConfirm={confirmarDialogo} />
    </AppShell>
  );
}
